import React, {
  CSSProperties,
  JSXElementConstructor,
  useRef,
  useState
} from 'react';
import Input from '../Input/Input';
import Checkbox from '../Checkbox/Checkbox';
import Typography from '../../commonComponents/Typography/Typography';
import Grid from '../../layoutComponents/Grid';
import {
  StyledSearchBox,
  StyledSearchList,
  StyledSearchListFooter,
  StyledSearchOption
} from './SearchBox.style';
import {
  Action,
  ActionTypes,
  IMultiselectOption,
  Interactions,
  ISearchboxOption,
  ISearchBoxProps,
  ISearchBoxState,
  ISearchContext,
  ISearchInputProps,
  ISearchListProps,
  ISearchOptionProps,
  NavType,
  SelectAllProps,
  VisibleStates
} from './types';
import {
  accumulateHeight,
  computeScrollTop,
  deriveMultiSelectValue,
  deriveNextMultiSelections,
  deriveOptionsHeight,
  deriveSearchListHeight,
  deriveSelectionIndex,
  deriveSingleSelectValue,
  readMultiSelections,
  shouldRenderMultiSelections
} from './utils';

const Context = React.createContext<ISearchContext | null>(null);

/**
 * Hook that returns SearchBox context.
 * If null throw
 *
 * @returns ISearchContext
 */
function useSearchContext(): ISearchContext {
  const context = React.useContext(Context);
  if (context === null) {
    throw new Error('Must be used within a `SearchBox`');
  }

  return context;
}

/**
 * Hook that sets up an effect to focus the input after a selection.
 */
function useFocusManagement(
  element: React.MutableRefObject<HTMLInputElement | null>,
  selectedMultiValues: ISearchboxOption[],
  previousAction?: ActionTypes
): void {
  React.useEffect(() => {
    if (
      (previousAction && previousAction === ActionTypes.SELECT_MULTI_CLICK) ||
      (previousAction && previousAction === ActionTypes.SELECT_CLICK)
    ) {
      if (element.current !== null) element.current.focus();
    }
  }, [element, previousAction, selectedMultiValues]);
}

function reducer(state: ISearchBoxState, action: Action): ISearchBoxState {
  const previousAction = action.type;
  switch (action.type) {
    case ActionTypes.CHANGE:
      return {
        ...state,
        interactionState: Interactions.ACTING,
        currentKeyboardValue: null,
        currentInputValue: action.payload,
        previousAction
      };
    case ActionTypes.INITIAL_KEYBOARD_NAV:
      return {
        ...state,
        interactionState: Interactions.NAVIGATING,
        currentKeyboardValue: null,
        previousAction
      };
    case ActionTypes.KEYBOARD_NAV:
      return {
        ...state,
        interactionState: Interactions.NAVIGATING,
        currentKeyboardValue: action.payload,
        currentScrollTop: computeScrollTop(state, action.payload),
        previousAction
      };
    case ActionTypes.SELECT_CLICK:
      return {
        ...state,
        interactionState: Interactions.IDLE,
        currentKeyboardValue: null,
        currentInputValue: action.payload,
        previousAction
      };
    case ActionTypes.SELECT_MULTI_CLICK:
      return {
        ...state,
        interactionState: Interactions.ACTING,
        currentInputValue: '',
        selectedMultiValues: action.payload,
        previousAction
      };
    case ActionTypes.SELECT_MULTI_KEYBOARD:
      return {
        ...state,
        interactionState: Interactions.ACTING,
        currentInputValue: '',
        selectedMultiValues: action.payload,
        previousAction
      };
    case ActionTypes.SELECT_KEYBOARD:
      return {
        ...state,
        interactionState: Interactions.IDLE,
        currentKeyboardValue: null,
        currentInputValue: state.currentKeyboardValue
          ? state.currentKeyboardValue.label
          : '',
        previousAction
      };
    case ActionTypes.CLEAR:
      return {
        ...state,
        interactionState: Interactions.IDLE,
        currentKeyboardValue: null,
        currentInputValue: '',
        previousAction
      };
    case ActionTypes.INTERACT:
      return {
        ...state,
        interactionState: Interactions.ACTING,
        currentKeyboardValue: null,
        previousAction
      };
    case ActionTypes.FOCUS_MULTI:
      return {
        ...state,
        interactionState: Interactions.IDLE,
        currentKeyboardValue: null,
        currentInputValue: '',
        previousAction
      };
    case ActionTypes.BLUR_MULTI:
      // When bluring a multi select, reset currentInputValue to ''
      return {
        ...state,
        interactionState: Interactions.IDLE,
        currentInputValue: '',
        currentKeyboardValue: null,
        previousAction
      };
    case ActionTypes.BLUR:
    case ActionTypes.ESCAPE:
      return {
        ...state,
        interactionState: Interactions.IDLE,
        currentKeyboardValue: null,
        previousAction
      };
    case ActionTypes.CONTINUE:
      // react can bail out of noop updates
      return state;
    case ActionTypes.LOAD:
      return {
        ...state,
        optionsHeight: deriveOptionsHeight(state, action.payload),
        searchListHeight: deriveSearchListHeight(state, action.payload),
        searchListRows: action.payload.metaData.searchListRows
      };
    case ActionTypes.SCROLL:
      return {
        ...state,
        currentScrollTop: action.payload.scrollTop
      };
    default:
      return state;
  }
}

const SELECT_ALL = '__*';

/**
 * State Container,
 * SearchBox is responsible for storing and distributing all internal state through context
 */
export const SearchBox: React.FC<ISearchBoxProps> = ({
  children,
  onSearch,
  onChange,
  onClick,
  onBlur,
  onFocus,
  value: controlledValue,
  multi,
  placeholder,
  userSelectOnClick,
  openOnClick,
  icon,
  disabled,
  hasError,
  required,
  inputId,
  delimiter = ',',
  ...props
}) => {
  /* ref to searchbox input */
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  /* ref to optionList */
  const optionListRef = React.useRef<HTMLUListElement | null>(null);
  /* list of option labels and user values */
  const optionsRef = React.useRef<ISearchboxOption[] | null>(null);
  /* is searchbox mounted */
  const isMounted = React.useRef<boolean>(false);

  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [state, dispatch] = React.useReducer(reducer, {
    currentInputValue: '',
    interactionState: Interactions.IDLE,
    currentKeyboardValue: null,
    selectedMultiValues: [],
    currentScrollTop: 0,
    optionsHeight: []
  });

  useFocusManagement(inputRef, state.selectedMultiValues, state.previousAction);

  const context = React.useMemo(() => {
    return {
      inputRef,
      optionsRef,
      optionListRef,
      isMounted,
      controlledValue,
      state,
      multi,
      dispatch,
      onChange,
      hasError,
      isVisible: state.interactionState in VisibleStates
    };
  }, [state, controlledValue, multi, onChange, hasError]);
  return (
    <Context.Provider value={context}>
      <StyledSearchBox
        fontSize={0}
        {...props}
        role="combobox"
        aria-haspopup="listbox"
        aria-controls="listbox"
        aria-expanded={context.isVisible}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
        <SearchInput
          inputId={inputId}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          icon={icon}
          delimiter={delimiter}
          onClick={onClick}
          onFocus={onFocus}
          onBlur={onBlur}
          onSearch={onSearch}
          userSelectOnClick={userSelectOnClick}
          openOnClick={openOnClick}
          isOpen={context.isVisible}
          {...(props as any)}
        />
        {children}
      </StyledSearchBox>
    </Context.Provider>
  );
};
/**
 * SearchInput controls focus management and keyboard navigation / selection
 */
const SearchInput: React.FC<ISearchInputProps> = ({
  placeholder,
  required,
  disabled,
  userSelectOnClick,
  openOnClick,
  icon,
  isOpen,
  onClick,
  onBlur,
  onFocus,
  onSearch,
  inputId,
  delimiter,
  ...props
}) => {
  const {
    state,
    controlledValue,
    inputRef,
    optionsRef,
    optionListRef,
    isMounted,
    dispatch,
    multi,
    hasError,
    onChange,
    isVisible
  } = useSearchContext();

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    /**
     * User has cleared with the backspace key, or sent,
     * fire a clear event
     */
    if (e.target.value.trim() === '') {
      dispatch({ type: ActionTypes.CLEAR });
      // invoke users onChange to indicate a cleared input
      if (onChange && !multi) {
        onChange(null);
      }
    } else {
      dispatch({ type: ActionTypes.CHANGE, payload: e.target.value });
    }
    // invoke users onSearch callback with the current targetValue
    if (onSearch) {
      onSearch(e.target.value);
    }
  }

  function handleKeydown(e: React.KeyboardEvent<HTMLInputElement>): void {
    const { current } = optionsRef;

    // exit if we have no options
    if (!current || current.length === 0) {
      return;
    }
    const selectAllOption = optionListRef.current?.querySelector(
      `input[type='checkbox'][value='${SELECT_ALL}']`
    );
    const indexOfSelectAllOption = Array.from(
      optionListRef.current?.querySelectorAll('input[type="checkbox"]') || []
    ).findIndex(node => (node as HTMLInputElement).value === SELECT_ALL);

    const searchOptions = [...current];
    if (multi && selectAllOption) {
      searchOptions.splice(indexOfSelectAllOption, 0, {
        label: selectAllOption.querySelector('span')?.textContent as string,
        value: SELECT_ALL,
        metaData: { index: indexOfSelectAllOption }
      });
      searchOptions.forEach((option, index) => {
        // eslint-disable-next-line no-param-reassign
        option.metaData.index = index;
      });
    }
    const currentSelectionIndex = deriveSelectionIndex(
      searchOptions,
      state.currentKeyboardValue
    );

    let nextIndex: number;
    let navType: NavType;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        /**
         * When navigating a closed list,
         * initial nav should open list with no
         * options selected
         */
        if (state.interactionState === Interactions.IDLE) {
          dispatch({ type: ActionTypes.INITIAL_KEYBOARD_NAV });
          break;
        }
        /**
         * if we are at the end of the list, return to the top,
         * otherwise move selection to the next item
         */
        if (currentSelectionIndex + 1 > searchOptions.length - 1) {
          nextIndex = 0;
          navType = NavType.TOP;
        } else {
          nextIndex = currentSelectionIndex + 1;
          navType = NavType.DOWN;
        }

        dispatch({
          type: ActionTypes.KEYBOARD_NAV,
          payload: {
            ...searchOptions[nextIndex],
            metaData: {
              ...searchOptions[nextIndex].metaData,
              type: navType,
              nextIndex,
              searchListHeight: state.searchListHeight,
              searchListRows: state.searchListRows
            }
          }
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (state.interactionState === Interactions.IDLE) {
          dispatch({ type: ActionTypes.INITIAL_KEYBOARD_NAV });
          break;
        }

        /**
         * if we are at the top of the list, move to the bottom,
         * otherwise move selection to the next item
         */
        if (currentSelectionIndex - 1 < 0) {
          nextIndex = searchOptions.length - 1;
          navType = NavType.BOTTOM;
        } else {
          nextIndex = currentSelectionIndex - 1;
          navType = NavType.UP;
        }

        dispatch({
          type: ActionTypes.KEYBOARD_NAV,
          payload: {
            ...searchOptions[nextIndex],
            metaData: {
              ...searchOptions[nextIndex].metaData,
              type: navType,
              nextIndex,
              searchListHeight: state.searchListHeight,
              searchListRows: state.searchListRows
            }
          }
        });
        break;
      case 'Enter':
        if (
          (state.interactionState === Interactions.NAVIGATING ||
            state.interactionState === Interactions.ACTING) &&
          state.currentKeyboardValue !== null
        ) {
          e.preventDefault();
          let nextMultiValues: ISearchboxOption[] | undefined;
          if (multi) {
            const previousMultiValues = readMultiSelections(
              state,
              controlledValue as ISearchboxOption[]
            );

            nextMultiValues = deriveNextMultiSelections(
              previousMultiValues,
              state.currentKeyboardValue
            );

            if (state.currentKeyboardValue.value === SELECT_ALL) {
              const options = optionsRef.current as ISearchboxOption[];
              if (state.selectedMultiValues.length === options.length) {
                // all checked
                nextMultiValues = [];
              } else {
                nextMultiValues = options;
              }
            }

            dispatch({
              type: ActionTypes.SELECT_MULTI_KEYBOARD,
              payload: nextMultiValues
            });
          } else {
            dispatch({ type: ActionTypes.SELECT_KEYBOARD });
          }
          /**
           * invoke users onChange callback,
           * We pass the value prop, in this case we get the value prop from our refArray of search options.
           * As well as the list of currently selected multivalues.
           * If we are in single select nextMultiValues will be undefined
           */
          if (onChange && searchOptions[currentSelectionIndex]) {
            onChange(
              nextMultiValues || state.currentKeyboardValue,
              multi ? searchOptions[currentSelectionIndex] : undefined
            );
          }
        }
        break;
      case 'Escape':
        dispatch({ type: ActionTypes.ESCAPE });
        break;
      default:
        break;
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>): void {
    /**
     * we need to catch the browser before the next frame and before it paints.
     * This is because activeElement is assigned AFTER the blur event is processed.
     * We need the value before paint.
     * We want document.activeElement === li
     * Without RAf we will see document.activeElement === body
     *
     * Because of RAf we have to check if searchbox is mounted before firing dispatch
     */
    window.requestAnimationFrame(() => {
      if (
        inputRef.current !== document.activeElement &&
        optionListRef.current &&
        optionListRef.current.contains(document.activeElement)
      ) {
        // continue;
        if (isMounted.current) {
          dispatch({ type: ActionTypes.CONTINUE });
        }
      } else {
        // we have clicked outide the search bar
        if (isMounted.current) {
          if (multi) {
            dispatch({ type: ActionTypes.BLUR_MULTI });
          } else {
            dispatch({ type: ActionTypes.BLUR });
          }
        }
        // invoke users onBlur callback
        if (onBlur) {
          onBlur(e);
        }
      }
    });
  }

  function handleClick(e: React.MouseEvent<HTMLInputElement>): void {
    if (userSelectOnClick && inputRef.current) {
      inputRef.current.select();
    }

    if (multi) dispatch({ type: ActionTypes.FOCUS_MULTI });

    if (openOnClick) {
      if (isVisible) {
        dispatch({ type: ActionTypes.ESCAPE });
      } else {
        dispatch({ type: ActionTypes.INTERACT });
      }
    }

    // invoke users onClick
    if (onClick) {
      onClick(e);
    }
  }

  /**
   * Current Input value is derived base on
   * 1) Current interactionState
   * 2) Select mode Multi | Single
   * 3) Controlled or Uncontrolled
   */
  let inputValue: string;
  if (multi) {
    inputValue = deriveMultiSelectValue(
      state,
      delimiter,
      controlledValue as ISearchboxOption[]
    );
  } else {
    inputValue = deriveSingleSelectValue(
      state,
      controlledValue as ISearchboxOption
    );
  }

  return (
    <Input
      id={inputId}
      aria-label={inputId}
      type="text"
      autoComplete="off"
      value={inputValue}
      ref={inputRef}
      placeholder={placeholder}
      icon={icon}
      disabled={disabled}
      hasError={hasError}
      required={required}
      onChange={handleChange}
      onKeyDown={handleKeydown}
      onBlur={handleBlur}
      onClick={handleClick}
      onFocus={onFocus}
      borderColor={isOpen ? 'form.input.border.focus' : undefined}
      {...(props as any)}
    />
  );
};

export const SelectAll: React.FC<SelectAllProps> = ({
  label = 'Select All'
}) => {
  const { state, optionsRef, dispatch, onChange } = useSearchContext();

  // This key is used to force refresh the SelectAll whenever the 'optionsRef'
  // value is changed, otherwise, the value of optionsRef may be out-dated and
  // may affect the derived values such as isIntermediate.
  const [key, setKey] = React.useState(0);

  const isActive = Boolean(state.currentKeyboardValue?.value === SELECT_ALL);
  const isChecked = Boolean(state.selectedMultiValues?.length > 0);
  const isIntermediate = Boolean(
    isChecked &&
      state.selectedMultiValues?.length !== optionsRef.current?.length
  );

  const handleClick = (): void => {
    if (optionsRef.current) {
      let nextMultiSelections: ISearchboxOption[] | null;

      if (isChecked && !isIntermediate) {
        // all checked
        nextMultiSelections = [];
      } else {
        // unchecked or partially checked
        nextMultiSelections = optionsRef.current;
      }

      dispatch({
        type: ActionTypes.SELECT_MULTI_CLICK,
        payload: nextMultiSelections
      });

      if (onChange) {
        onChange(nextMultiSelections);
      }
    }
  };

  // This is a force refresh technique to synchronize the potentially out-dated
  // reference value.
  React.useEffect(() => {
    setKey(x => x + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsRef.current]);

  if (optionsRef.current?.length === 0) {
    return null;
  }

  return (
    <StyledSearchOption
      key={key}
      p={2}
      color="primary.0"
      role="option"
      aria-selected={isActive}
      onClick={handleClick}
      tabIndex={-1}
    >
      <Grid alignItems="center">
        <Checkbox
          id={label}
          value={SELECT_ALL}
          type="checkbox"
          checked={isChecked}
          intermediate={isIntermediate}
          tabIndex={-1}
          style={{
            marginRight: '10px',
            pointerEvents: 'none' // Let the state dictate whether checkbox is checked, not user clicks
          }}
        />
        <span>{label}</span>
      </Grid>
    </StyledSearchOption>
  );
};

/**
 * Wrapper around searchOptions
 * Controls the reference to the list of options,
 * this list is reset after ever render to ensure we get a clean list of options.
 *
 * In multi select mode SearchList is responsible for partitioning selected options away from
 * new options
 */
export const SearchList: React.FC<ISearchListProps> = props => {
  const {
    isVisible,
    optionListRef,
    optionsRef,
    multi,
    state,
    controlledValue,
    hasError,
    dispatch
  } = useSearchContext();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [displayedSelectedValues, setDisplayedSelectedValues] = useState<
    ISearchboxOption[] | null
  >(null);

  React.useEffect(() => {
    // If we're entering an idling state, its time to update our displayedSelectedValues.
    // This will ensure that the next time the searchlist is opened, selected values will be moved to the top
    if (state.interactionState === Interactions.IDLE) {
      setDisplayedSelectedValues(
        readMultiSelections(state, controlledValue as ISearchboxOption[])
      );
    }
  }, [state, controlledValue, state.selectedMultiValues]);

  /**
   * Setup options array on every render
   * this needs to run before the children are rendered
   */
  React.useLayoutEffect(() => {
    optionsRef.current = [];
    /**
     * make sure to reset if SearchList unmounts,
     * otherwise we may get lingering stale options
     */
    return () => {
      optionsRef.current = [];
    };
  });

  React.useLayoutEffect(() => {
    if (scrollContainerRef.current !== null) {
      scrollContainerRef.current.scrollTop = state.currentScrollTop;

      if (props.onScroll && state.searchListHeight) {
        const { currentScrollTop, searchListHeight } = state;

        const scrollData = {
          scrollTop: currentScrollTop,
          visibleHeight: searchListHeight,
          totalHeight: accumulateHeight(
            state.optionsHeight,
            state.optionsHeight.length - 1
          )
        };

        props.onScroll(scrollData);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state.currentScrollTop, state.searchListHeight]);

  React.useEffect(() => {
    dispatch({
      type: ActionTypes.LOAD,
      payload: {
        label: '',
        value: '',
        metaData: {
          searchListHeight: props.searchListHeight,
          searchListRows: props.searchListRows
        }
      }
    });
  }, [dispatch, props]);

  const hidden = !isVisible;
  let selectedChildren: JSX.Element[] | null = null;
  let childrenToRender = props.children;

  let selectAllOption: JSX.Element | null = null;
  const options: JSX.Element[] = [];

  if (multi) {
    selectedChildren =
      displayedSelectedValues &&
      displayedSelectedValues.map((selection, index) => {
        return (
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          <SearchOption
            key={index}
            label={selection.label}
            value={selection.value}
            metaData={selection.metaData}
          />
        );
      });

    React.Children.forEach(props.children, (child: React.ReactElement) => {
      if ((child?.type as JSXElementConstructor<any>)?.name === 'SelectAll') {
        selectAllOption = child;
      } else {
        options.push(child);
      }
    });

    childrenToRender = React.Children.map(
      options,
      (child: React.ReactElement) => {
        /**
         * Don't render already selected options,
         * These will be rendered in the selected children array.
         * This is updated once every time the menu closes
         */
        if (
          child &&
          displayedSelectedValues &&
          displayedSelectedValues.some(
            option => option.value === child.props.value
          )
        ) {
          return null;
        }
        return child;
      }
    );
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const element = event.target as HTMLDivElement;
    const { scrollTop } = element;

    dispatch({
      type: ActionTypes.SCROLL,
      payload: {
        scrollTop
      }
    });
  };

  let scrollStyle: CSSProperties = {};

  if (props.searchListRows) {
    scrollStyle = {
      overflowY: 'auto',
      maxHeight: props.searchListRows * (state.optionsHeight[0] || 0)
    };
  } else if (props.searchListHeight) {
    scrollStyle = {
      overflowY: 'auto',
      maxHeight: props.searchListHeight
    };
  }

  return (
    <StyledSearchList
      boxShadow="md"
      hidden={hidden}
      ref={optionListRef}
      hasError={hasError}
      role="listbox"
    >
      <div
        style={scrollStyle}
        role="list"
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {selectAllOption}
        {multi && shouldRenderMultiSelections(displayedSelectedValues) && (
          <div>{selectedChildren}</div>
        )}
        {childrenToRender}
      </div>
      {props.footerContent && (
        <StyledSearchListFooter>
          <Typography kind="overline">{props.footerContent}</Typography>
        </StyledSearchListFooter>
      )}
    </StyledSearchList>
  );
};

const MultiselectOption: React.FC<IMultiselectOption> = ({ label, value }) => {
  const { state, controlledValue } = useSearchContext();
  const selectedValuesToRead = readMultiSelections(
    state,
    controlledValue as ISearchboxOption[]
  );

  const isChecked = Boolean(
    selectedValuesToRead &&
      selectedValuesToRead.some(option => option.value === value)
  );

  return (
    <Grid alignItems="center">
      <Checkbox
        id={label}
        type="checkbox"
        checked={isChecked}
        tabIndex={-1}
        style={{
          marginRight: '10px',
          pointerEvents: 'none' // Let the state dictate whether checkbox is checked, not user clicks
        }}
      />
      <span>{label}</span>
    </Grid>
  );
};
/**
 * Renders Li element to be selected, manages selected state during keyboard nav,
 * Adds its props to the option ref used in keyboard navigation
 */
export const SearchOption: React.FC<ISearchOptionProps> = ({
  label,
  value,
  metaData = {},
  onClick,
  ...props
}) => {
  const searchOptionRef = useRef<HTMLLIElement>(null);
  const {
    state,
    optionsRef,
    hasError,
    onChange,
    multi,
    controlledValue,
    dispatch
  } = useSearchContext();

  const isActive = Boolean(
    state.currentKeyboardValue && state.currentKeyboardValue.value === value
  );
  const optionProps: ISearchboxOption = { label, value, metaData };

  React.useEffect(() => {
    /**
     * Push its option props into List of options
     * In the future, we may want to investigate another way of handling this
     */
    if (optionsRef.current) {
      optionsRef.current.push(optionProps);
    }
  });

  React.useLayoutEffect(() => {
    if (searchOptionRef.current !== null) {
      const optionHeight = searchOptionRef.current.getBoundingClientRect()
        .height;
      const { index } = metaData;

      // Don't support scrolling if index is missing
      if (index === undefined) {
        return;
      }

      if (
        (state.optionsHeight[index] === 0 ||
          state.optionsHeight[index] === undefined) &&
        optionHeight > 0
      ) {
        dispatch({
          type: ActionTypes.LOAD,
          payload: {
            label,
            value,
            metaData: {
              index,
              optionHeight,
              searchListHeight: state.searchListHeight,
              searchListRows: state.searchListRows
            }
          }
        });
      }
    }
  });

  function handleClick(e: React.MouseEvent<HTMLElement>): void {
    let nextMultiValues;
    if (multi) {
      // Get the previously selected values from state
      const previousMultiValues = readMultiSelections(
        state,
        controlledValue as ISearchboxOption[]
      );
      // Create new array of selected values adding the new value
      nextMultiValues = deriveNextMultiSelections(
        previousMultiValues,
        optionProps
      );

      dispatch({
        type: ActionTypes.SELECT_MULTI_CLICK,
        payload: nextMultiValues
      });
    } else {
      dispatch({ type: ActionTypes.SELECT_CLICK, payload: label });
    }

    // invoke users onClick
    if (onClick) {
      onClick(e);
    }
    /**
     * invoke users onChange callback,
     * We pass the value prop as well as the list
     * of currently selected multivalues.  If we are in single select
     * nextMultiValues will be undefined and we pass the label
     */
    if (onChange) {
      onChange(nextMultiValues || optionProps, multi ? optionProps : undefined);
    }
  }

  return (
    <StyledSearchOption
      p={2}
      role="option"
      {...props}
      hasError={hasError}
      aria-selected={isActive}
      onClick={handleClick}
      tabIndex={-1}
      ref={searchOptionRef}
    >
      {multi ? (
        <MultiselectOption label={label} value={value} {...props} />
      ) : (
        label
      )}
    </StyledSearchOption>
  );
};
